import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

type RequirePartnerAuthProps = {
  children: React.ReactNode;
};

export default function RequirePartnerAuth({ children }: RequirePartnerAuthProps) {
  const { user, firebaseUser, isAdmin, isLoading } = useAuth();
  const location = useLocation();
  const [authChecked, setAuthChecked] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  
  // Check URL for partner parameter (for admin access)
  const urlParams = new URLSearchParams(location.search);
  const partnerParam = urlParams.get('partner');
  
  // Check if user is a partner (has partnerCode) or admin with partner param
  const isPartner = !!user?.partnerCode || (isAdmin && !!partnerParam);
  
  // For debugging purposes, log every mount/re-render
  console.log("RequirePartnerAuth render:", { 
    path: location.pathname,
    search: location.search,
    partnerParam,
    isLoading, 
    isAdmin, 
    isPartner, 
    uid: user?.uid,
    role: user?.role,
    partnerCode: user?.partnerCode,
    isAuthenticated: !!user && !!firebaseUser
  });

  useEffect(() => {
    if (!isLoading) {
      const checkPartnerStatus = async () => {
        console.log("Checking partner auth status with:", { 
          isLoading, 
          user, 
          firebaseUser, 
          isAdmin, 
          isPartner,
          path: location.pathname
        });
        
        if (!user || !firebaseUser) {
          console.log("Auth state: Not authenticated", { user, firebaseUser });
          setAuthError("You must be logged in to access this page.");
        } else if (!isPartner && !isAdmin) {
          console.log("Auth state: Not Partner or admin", { 
            userRole: user.role, 
            partnerCode: user.partnerCode,
            uid: user.uid,
            email: user.email
          });
          
          // Check if this is likely a partner account but missing the partner code
          if (user.role === 'partner' || user.role?.includes('partner')) {
            setAuthError("Your account appears to be a partner account but is missing partner permissions. This is likely a configuration issue. Please contact support for assistance.");
            toast.error("Your partner account needs to be configured correctly. Please contact support.");
            
            // Add more debug information
            console.error(`Partner authentication failure: User ${user.uid} (${user.email}) has role '${user.role}' but missing partnerCode`);
          } else {
            setAuthError("You don't have permission to access this partner section. Partner access required.");
          }
        } else {
          console.log("Auth state: Authenticated Partner or admin", { 
            isAdmin, 
            isPartner, 
            userRole: user.role, 
            partnerCode: user.partnerCode,
            uid: user.uid
          });
        }
        setAuthChecked(true);
      };
      
      checkPartnerStatus();
    }
  }, [user, firebaseUser, isPartner, isAdmin, isLoading, location.pathname]);

  // Show loading state while checking auth
  if (isLoading || !authChecked) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Verifying authentication...</p>
        </div>
      </div>
    );
  }
  
  // Show error state
  if (authError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md p-6 bg-red-50 rounded-lg border border-red-200">
          <h2 className="text-lg font-medium text-red-800 mb-2">Authentication Error</h2>
          <p className="text-red-700 mb-4">{authError}</p>
          <div className="space-y-2">
            <button 
              onClick={() => window.location.href = "/login"}
              className="bg-red-700 text-white px-4 py-2 rounded-md hover:bg-red-800 transition-colors w-full"
            >
              Return to Login
            </button>
            <button 
              onClick={() => window.location.href = "/"}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors w-full"
            >
              Go to Homepage
            </button>
          </div>
          {process.env.NODE_ENV !== 'production' && (
            <div className="mt-4 p-2 bg-gray-100 rounded text-left">
              <p className="text-xs font-mono text-gray-600">Debug info:</p>
              <pre className="text-xs font-mono text-gray-600 overflow-auto max-h-32">
                {JSON.stringify({ 
                  user: user ? { 
                    uid: user.uid,
                    email: user.email,
                    role: user.role,
                    partnerCode: user.partnerCode
                  } : null,
                  isAdmin,
                  isPartner,
                  path: location.pathname
                }, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Not authenticated, redirect to login
  if (!user || !firebaseUser) {
    console.log("Redirecting to login, user not authenticated", { user, firebaseUser });
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has pending email verification
  if (user.verificationData && !user.isEmailVerified) {
    console.log("Redirecting to verify email", { verificationData: user.verificationData, isEmailVerified: user.isEmailVerified });
    return <Navigate to={`/verify-email?email=${encodeURIComponent(user.email || '')}&name=${encodeURIComponent(user.name || '')}`} replace />;
  }

  // If authenticated and user is a partner or admin, render the children
  if (user && firebaseUser && (isPartner || isAdmin)) {
    console.log("Rendering partner protected content", { isPartner, isAdmin, path: location.pathname });
    return <>{children}</>;
  }
  
  // Otherwise, redirect to home page
  console.log("Redirecting to home, not partner or admin", { isPartner, isAdmin, user });
  return <Navigate to="/" replace />;
}
